import React, { useEffect, useState, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Box, TextField, Grid, Typography, Button, Autocomplete, CircularProgress } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { setFormData } from '../slices/donorinfoSlice';
import { nextComponent } from '../slices/navigatorSlice';
import { postgridService } from '../service/postgridService';
import StripeExpressCheckoutWrapper from './StripeExpressCheckoutWrapper';

const DonorInfoForm = ({ onFormSubmit }) => {
    const [loading, setLoading] = useState(false);
    const { handleSubmit, control, reset, formState: { errors }, setValue, getValues } = useForm();
    const dispatch = useDispatch();
    const formData = useSelector((state) => state.form);
    const [addressSuggestions, setAddressSuggestions] = useState([]);
    const debounceRef = useRef(null);
    const requestIdRef = useRef(0);

    useEffect(() => {
        // Load form data from Redux store
        reset(formData);
    }, [formData, reset]);

    const onSubmit = (data) => {
        // Save form data to Redux store
        dispatch(setFormData(data));
        dispatch(nextComponent());
        // onFormSubmit();  // Call the handler passed from the parent component
    };

    /**
     * Debounced type-ahead. `reason` guards against MUI firing onInputChange
     * with reason='reset' when a suggestion is selected — without this guard,
     * selecting an option re-triggers a search for the selected text.
     */
    const handleAddressChange = (event, value, reason) => {
        if (reason === 'reset') return;

        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }

        debounceRef.current = setTimeout(async () => {
            if (value && value.trim().length >= 3) {
                setLoading(true);
                const currentRequest = ++requestIdRef.current;
                try {
                    const results = await postgridService.findByKeyword(value);
                    if (currentRequest !== requestIdRef.current) return; // stale response
                    setAddressSuggestions(results || []);
                } catch (error) {
                    console.error(error);
                    if (currentRequest === requestIdRef.current) {
                        setAddressSuggestions([]);
                    }
                } finally {
                    if (currentRequest === requestIdRef.current) {
                        setLoading(false);
                    }
                }
            } else {
                setAddressSuggestions([]);
            }
        }, 200);
    };

    /**
     * On selection: verify against Canada Post via PostGrid, then populate
     * ALL fields from the VERIFIED result — including address line 1, which
     * comes back in Canada Post format ("2-106 RAILWAY AVE"), and the FULL
     * postal code (the dropdown preview only carries the 3-char FSA).
     */
    const handleAddressSelect = async (event, value) => {
        // Cleared, or freeSolo text typed without selecting a suggestion —
        // keep whatever string is there and let the donor fill fields manually.
        if (!value || typeof value === 'string') {
            setValue('address', value || '', { shouldValidate: true });
            return;
        }

        // Optimistic fill from the preview while verification runs
        setValue('address', value.Text, { shouldValidate: true });
        setValue('city', value.City || '');
        setValue('province', value.ProvinceCode || '');
        setValue('pincode', '');
        try {
            // VerifyText = PostGrid's original preview string — the safest
            // parser input. (value.Text is our display-transformed version.)
            const verified = await postgridService.verifyAddress(
                value.VerifyText || value.Text,
                value.City || value.RawData?.preview?.city || ''
            );

            if (verified.status === 'failed') {
                console.warn('PostGrid could not verify the selected address:', verified.errors);
                return; // keep the optimistic preview values
            }

            // Note: verifyAddress now returns a FLAT object — no `.data` wrapper.
            setValue('address', verified.line1, { shouldValidate: true }); // "2-106 RAILWAY AVE"
            setValue('city', verified.city, { shouldValidate: true });
            setValue('province', verified.provinceCode, { shouldValidate: true });
            setValue('pincode', verified.postalCode, { shouldValidate: true }); // full code, not FSA
        } catch (error) {
            console.error('Address verification failed:', error);
            // Preview values remain; donor can correct city/postal manually.
        }
    };

    return (
        <>
            <div className='checkout-option-header'>
                <Typography variant='formTitle' sx={{ fontSize: { lg: "3.5vw", md: "3.5vw", sm: "3.5vw", xs: "3.5vw" }, p: 2 }} component="strong" gutterBottom>
                    Donate with
                </Typography>
            </div>
            <div className="express-checkout">
                <StripeExpressCheckoutWrapper />
            </div>
            <div className='checkout-option-text'>
                <Typography variant='formTitle' sx={{ fontSize: { lg: "3vw", md: "3vw", sm: "3vw", xs: "3vw" }, p: 2 }} component="strong" gutterBottom>
                    OR
                </Typography>
            </div>
            <Box sx={{ p: 0 }}>
                <Typography variant='formTitle' sx={{ fontSize: { lg: "3vw", md: "3vw", sm: "3vw", xs: "3vw" }, p: 2 }} component="p" gutterBottom>
                    Enter your information
                </Typography>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Grid container spacing={1}>
                        <Grid item xs={6}>
                            <Controller
                                name="firstName"
                                control={control}
                                defaultValue=""
                                rules={{ required: 'First Name is required' }}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label="First Name"
                                        variant="outlined"
                                        fullWidth
                                        size='small'
                                        error={!!errors.firstName}
                                        helperText={errors.firstName ? errors.firstName.message : ''}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <Controller
                                name="lastName"
                                control={control}
                                defaultValue=""
                                rules={{ required: 'Last Name is required' }}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label="Last Name"
                                        variant="outlined"
                                        fullWidth
                                        size='small'
                                        error={!!errors.lastName}
                                        helperText={errors.lastName ? errors.lastName.message : ''}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Controller
                                name="email"
                                control={control}
                                defaultValue=""
                                rules={{
                                    required: 'Email Address is required',
                                    pattern: {
                                        value: /^[^@ ]+@[^@ ]+\.[^@ .]{2,}$/,
                                        message: 'Email Address is not valid',
                                    },
                                }}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label="Email Address"
                                        variant="outlined"
                                        fullWidth
                                        size='small'
                                        error={!!errors.email}
                                        helperText={errors.email ? errors.email.message : ''}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Controller
                                name="address"
                                control={control}
                                defaultValue=""
                                rules={{ required: 'Address Line 1 is required' }}
                                render={({ field }) => (
                                    <Autocomplete
                                        freeSolo
                                        // Controlled by react-hook-form: the field value is the
                                        // string in the input, options are suggestion objects.
                                        inputValue={field.value || ''}
                                        options={addressSuggestions}
                                        loading={loading}
                                        // Suggestions are already filtered server-side by PostGrid;
                                        // MUI's client-side filter would hide valid options because
                                        // "2-106 RAILWAY AVE" doesn't start with what the donor typed.
                                        filterOptions={(x) => x}
                                        getOptionLabel={(option) => {
                                            if (!option) return "";
                                            if (typeof option === "string") return option;
                                            return option.Text || "";
                                        }}
                                        onInputChange={(event, newValue, reason) => {
                                            // Keep react-hook-form in sync with what's typed
                                            if (reason !== 'reset') {
                                                field.onChange(newValue);
                                            }
                                            handleAddressChange(event, newValue, reason);
                                        }}
                                        onChange={handleAddressSelect}
                                        renderOption={(props, option) => (
                                            <li {...props} key={option.Id}>
                                                <Typography variant="body2">
                                                    {option.Text}
                                                </Typography>
                                            </li>
                                        )}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Address Line 1"
                                                variant="outlined"
                                                fullWidth
                                                size='small'
                                                error={!!errors.address}
                                                helperText={errors.address ? errors.address.message : ''}
                                                InputProps={{
                                                    ...params.InputProps,
                                                    endAdornment: (
                                                        <>
                                                            {loading ? <CircularProgress color="inherit" size={16} /> : null}
                                                            {params.InputProps.endAdornment}
                                                        </>
                                                    ),
                                                }}
                                            />
                                        )}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Controller
                                name="province"
                                control={control}
                                defaultValue=""
                                rules={{ required: 'Province is required' }}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label="Province"
                                        variant="outlined"
                                        fullWidth
                                        size='small'
                                        error={!!errors.province}
                                        helperText={errors.province ? errors.province.message : ''}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <Controller
                                name="city"
                                control={control}
                                defaultValue=""
                                rules={{ required: 'City is required' }}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label="City"
                                        variant="outlined"
                                        fullWidth
                                        size='small'
                                        error={!!errors.city}
                                        helperText={errors.city ? errors.city.message : ''}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <Controller
                                name="pincode"
                                control={control}
                                defaultValue=""
                                rules={{
                                    required: 'Postal Code is required',
                                    pattern: {
                                        // Canada Post format: A1A 1A1 (space optional on input)
                                        value: /^[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/,
                                        message: 'Postal Code must be in A1A 1A1 format',
                                    },
                                }}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label="Postal Code"
                                        variant="outlined"
                                        fullWidth
                                        size='small'
                                        error={!!errors.pincode}
                                        helperText={errors.pincode ? errors.pincode.message : ''}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Button variant="contained" color="primary" type="submit" sx={{ marginTop: 2 }} fullWidth>
                                Continue
                            </Button>
                        </Grid>
                    </Grid>
                </form>
            </Box>
        </>
    );
};

export default DonorInfoForm;
